import { Timestamp } from 'firebase/firestore';
import {
  ErrorDocument,
  ErrorSeverity,
  HealthFeedState,
  ServiceStatusDoc,
  ServiceStatusEntry,
} from '@/types';

/**
 * Development-only sample data for viewing states the live feed cannot show.
 *
 * `serviceStatus` is not deployed yet, so a real browser only ever reaches the
 * "Status Unavailable" path. These fixtures substitute the INPUT to the real
 * calculator — they do not alter how it scores anything, so what renders under
 * `?demo=` is the production code path fed synthetic occurrences.
 *
 * Guarded by `import.meta.env.DEV`, so the whole module is dead code in a
 * production build and gets dropped by the bundler.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

function entry(minutesAgo: number, sev: ErrorSeverity): ServiceStatusEntry {
  return { ts: Timestamp.fromDate(new Date(Date.now() - minutesAgo * MINUTE)), sev };
}

function doc(service: string, recent: ServiceStatusEntry[]): ServiceStatusDoc {
  return { service, recent, updatedAt: Timestamp.now() };
}

/** Spread `count` occurrences across the given span, oldest last. */
function spread(count: number, fromMinutes: number, toMinutes: number, sev: ErrorSeverity) {
  const step = (fromMinutes - toMinutes) / Math.max(1, count - 1);
  return Array.from({ length: count }, (_, i) => entry(fromMinutes - i * step, sev));
}

export interface DemoScenario {
  aggregates: ServiceStatusDoc[];
  feedState: HealthFeedState;
  feedError?: Error;
  /** Sample admin feed rows; omit to keep the real (locked) feed. */
  errors?: ErrorDocument[];
}

function sampleError(
  id: string,
  fields: Partial<ErrorDocument> & Pick<ErrorDocument, 'service' | 'severity' | 'message'>
): ErrorDocument {
  return {
    id,
    hash: `h_${id}`,
    source: 'backend',
    count: 1,
    resolved: false,
    timestamp: Timestamp.fromDate(new Date(Date.now() - 6 * 60 * MINUTE)),
    lastSeen: Timestamp.fromDate(new Date(Date.now() - 4 * MINUTE)),
    metadata: { environment: 'production' },
    ...fields,
  };
}

const SAMPLE_ERRORS: ErrorDocument[] = [
  sampleError('e1', {
    service: 'chat',
    severity: 'critical',
    status: 'investigating',
    message: 'Anthropic stream aborted mid-response (overloaded_error) after 3 retries',
    errorCode: 'ANTHROPIC_OVERLOADED',
    count: 112,
    timestamp: Timestamp.fromDate(new Date(Date.now() - 9 * 24 * 60 * MINUTE)),
    lastSeen: Timestamp.fromDate(new Date(Date.now() - 1 * MINUTE)),
    metadata: { environment: 'production', module: 'features.chat', requestId: 'req_8f21c0' },
    stack: 'Traceback (most recent call last):\n  File "app/features/chat/stream.py", line 214\n    async for chunk in provider.stream(...)\nanthropic.APIStatusError: Overloaded',
  }),
  sampleError('e2', {
    service: 'llm',
    severity: 'high',
    message: 'Gemini returned response.text is None (thinking budget consumed output tokens)',
    count: 9,
    metadata: { environment: 'production', module: 'infrastructure.llm.gemini' },
  }),
  sampleError('e3', {
    service: 'auth',
    severity: 'medium',
    status: 'resolved',
    message: 'Token refresh raced the outbound request and returned 401',
    count: 2,
    source: 'frontend',
    metadata: { environment: 'production', component: 'AuthContext', url: 'https://someta.app/chat' },
  }),
];

const permissionDenied = Object.assign(
  new Error('Missing or insufficient permissions.'),
  { code: 'permission-denied' }
);

export const DEMO_SCENARIOS: Record<string, DemoScenario> = {
  // Healthy, but with a real 24h history: a couple of stray failures hours ago
  // that never came close to a threshold.
  operational: {
    feedState: 'live',
    aggregates: [
      doc('chat', [entry(9 * 60, 'high'), entry(9 * 60 + 12, 'high')]),
      doc('llm', [entry(17 * 60, 'high')]),
      doc('pdf', spread(4, 22 * 60, 21 * 60, 'high')),
    ],
  },

  // One service over the degraded threshold now; another that WAS bad earlier
  // and healed on its own as its entries aged out of the scoring window.
  degraded: {
    feedState: 'live',
    aggregates: [
      doc('llm', [...spread(4, 41, 3, 'high'), ...spread(6, 20 * 60, 18 * 60, 'high')]),
      doc('chat', [entry(2 * 60 + 10, 'high'), ...spread(7, 5 * 60, 4 * 60, 'high')]),
      doc('audio', [entry(15 * 60, 'high')]),
      doc('subscriptions', spread(3, 23 * 60, 22 * 60, 'critical')),
    ],
  },

  // Critical failures in the last hour: outage, with collateral degradation.
  outage: {
    feedState: 'live',
    aggregates: [
      doc('chat', [...spread(5, 48, 1, 'critical'), ...spread(9, 3 * 60, 50, 'high')]),
      doc('llm', spread(4, 55, 6, 'high')),
      doc('firestore', [entry(12, 'high'), entry(38, 'high')]),
      doc('auth', spread(3, 7 * 60, 6 * 60, 'high')),
    ],
  },

  // The aggregate at its 200-entry cap: early hours were discarded and must not
  // be drawn as quiet.
  capped: {
    feedState: 'live',
    aggregates: [doc('chat', spread(200, 5 * HOUR / MINUTE, 2, 'high'))],
  },

  // Signed in as an admin: the detail feed populates alongside live health.
  admin: {
    feedState: 'live',
    aggregates: [doc('chat', spread(5, 48, 1, 'critical')), doc('llm', spread(4, 55, 6, 'high'))],
    errors: SAMPLE_ERRORS,
  },

  unavailable: { feedState: 'unavailable', aggregates: [], feedError: permissionDenied },
  connecting: { feedState: 'connecting', aggregates: [] },
};

/** Reads `?demo=<scenario>`; returns null outside development. */
export function readDemoScenario(): DemoScenario | null {
  if (!import.meta.env.DEV) return null;
  const name = new URLSearchParams(window.location.search).get('demo');
  return name ? DEMO_SCENARIOS[name] ?? null : null;
}
